import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS, Icon } from 'components/Icon';
import { Txt } from 'components/Txt';
import { theme } from 'lib/theme';
import React from 'react';
import { AddTemplateAction } from 'screens/AssessmentScreen/Explorer/ExplorerActionRow';
import { AddTemplateControl } from 'screens/AssessmentScreen/Explorer/ExplorerControlRow';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { Button, ButtonTheme } from 'components/Button';
import { LayoutChangeEvent, View } from 'react-native';
import {
  ActionSuggestion,
  ChecklistItem,
  ChecklistItemHasSuggestedActions,
  ChecklistItemHasSuggestedControls,
  ChecklistItemResult,
  ChecklistItemResultEnum,
  ControlSuggestion,
  InstanceParentParams,
} from 'store/assessments/types';

function getActionSuggestionsFromChecklistItem(
  checklistItemId: number,
  checklistItemResult: ChecklistItemResultEnum | null | undefined,
  itemHasSuggestion: ChecklistItemHasSuggestedActions,
): ActionSuggestion[] {
  if (checklistItemResult === null || checklistItemResult === undefined) {
    return [];
  }
  return (
    itemHasSuggestion[checklistItemId]?.[
      checklistItemResult === ChecklistItemResultEnum.cross
        ? 'negative'
        : checklistItemResult === ChecklistItemResultEnum.tick
          ? 'positive'
          : 'na'
    ] ?? []
  );
}

function getControlSuggestionsFromChecklistItem(
  checklistItemId: number,
  checklistItemResult: ChecklistItemResultEnum | null | undefined,
  itemHasSuggestion: ChecklistItemHasSuggestedControls,
): ControlSuggestion[] {
  if (checklistItemResult === null || checklistItemResult === undefined) {
    return [];
  }
  return (
    itemHasSuggestion[checklistItemId]?.[
      checklistItemResult === ChecklistItemResultEnum.cross
        ? 'negative'
        : checklistItemResult === ChecklistItemResultEnum.tick
          ? 'positive'
          : 'na'
    ] ?? []
  );
}

export const ChecklistItemRow: React.FC<{
  checklistItem: ChecklistItem;
  checklistItemResults: Record<string, ChecklistItemResult['result'] | null>;
  canAddActions: boolean;
  canAddControls: boolean;
  suggestedActionsByChecklistItem: ChecklistItemHasSuggestedActions;
  suggestedControlsByChecklistItem: ChecklistItemHasSuggestedControls;
  suggestionsUnderChecklistItems: boolean;
  handleSaveChecklistItemResult:
    | null
    | ((
        checklistItemId: number,
        newResult: ChecklistItemResultEnum | null,
      ) => void);
  questionId: string;
  fieldInstanceId: string | null;
  parent: InstanceParentParams;
}> = ({
  checklistItem,
  checklistItemResults,
  canAddActions,
  canAddControls,
  suggestedActionsByChecklistItem,
  suggestedControlsByChecklistItem,
  suggestionsUnderChecklistItems,
  handleSaveChecklistItemResult,
  questionId,
  fieldInstanceId,
  parent,
}) => {
  const [size, setSize] = React.useState<'small' | 'large'>('large');
  const sizeIsSmall = size === 'small';

  const checklistItemResult = checklistItemResults[checklistItem.id];
  const resultIsTick = checklistItemResult === ChecklistItemResultEnum.tick;
  const resultIsCross = checklistItemResult === ChecklistItemResultEnum.cross;
  const resultIsNA = checklistItemResult === ChecklistItemResultEnum.dash;

  const actionSuggestions = canAddActions
    ? getActionSuggestionsFromChecklistItem(
        checklistItem.id,
        checklistItemResult,
        suggestedActionsByChecklistItem,
      )
    : [];
  const controlSuggestions = canAddControls
    ? getControlSuggestionsFromChecklistItem(
        checklistItem.id,
        checklistItemResult,
        suggestedControlsByChecklistItem,
      )
    : [];

  const handleLayout = React.useCallback(
    (event: LayoutChangeEvent) =>
      setSize(event.nativeEvent.layout.width < 500 ? 'small' : 'large'),
    [],
  );

  return (
    <CardRow
      key={checklistItem.id}
      style={
        handleSaveChecklistItemResult
          ? {
              backgroundColor: checklistItemResult
                ? 'white'
                : theme.colors.white50,
            }
          : undefined
      }
    >
      <View
        style={{
          flexDirection: sizeIsSmall ? 'column' : 'row',
          alignItems: sizeIsSmall ? undefined : 'center',
        }}
        onLayout={handleLayout}
      >
        <View style={{ flexDirection: 'row', flex: 1 }}>
          {!handleSaveChecklistItemResult &&
            (checklistItemResult === ChecklistItemResultEnum.tick ? (
              <Icon
                source={ICONS.CheckLight}
                color={theme.colors.green}
                style={{ marginRight: theme.paddingHorizontal }}
              />
            ) : checklistItemResult === ChecklistItemResultEnum.cross ? (
              <Icon
                source={ICONS.XmarkLight}
                color={theme.colors.red}
                style={{ marginRight: theme.paddingHorizontal }}
              />
            ) : checklistItemResult === ChecklistItemResultEnum.dash ? (
              <Icon
                source={ICONS.DashLight}
                color="#999999"
                style={{ marginRight: theme.paddingHorizontal }}
              />
            ) : (
              <Icon
                source={ICONS.Blank}
                color="#999999"
                style={{ marginRight: theme.paddingHorizontal }}
              />
            ))}
          <Txt style={{ flex: 1 }}>{checklistItem.text}</Txt>
        </View>
        {handleSaveChecklistItemResult && (
          <ViewWithSeparators
            horizontal
            style={{
              flexDirection: 'row',
              alignItems: 'stretch',
              backgroundColor: '#f1f1f1',
              borderWidth: 1,
              borderColor: '#ddd',
              borderRadius: 4,
              overflow: 'hidden',
              flex: sizeIsSmall ? 1 : undefined,
              marginTop: sizeIsSmall ? theme.paddingHorizontalHalf : undefined,
              marginLeft: sizeIsSmall ? undefined : theme.paddingHorizontal,
            }}
            separator={<View style={{ width: 1, backgroundColor: '#ddd' }} />}
          >
            <Button
              icon={ICONS.CheckLight}
              iconColor={resultIsTick ? 'white' : theme.colors.green}
              style={{
                backgroundColor: resultIsTick ? theme.colors.green : 'white',
                borderWidth: 0,
                flex: sizeIsSmall ? 1 : undefined,
                width: sizeIsSmall ? undefined : 40,
                borderRadius: 0,
                justifyContent: 'center',
              }}
              styleHovered={{
                backgroundColor: resultIsTick
                  ? theme.colors.green80
                  : theme.colors.green10,
              }}
              stylePressed={{
                backgroundColor: resultIsTick
                  ? theme.colors.green90
                  : theme.colors.green20,
              }}
              onPress={
                handleSaveChecklistItemResult
                  ? () =>
                      handleSaveChecklistItemResult(
                        checklistItem.id,
                        ChecklistItemResultEnum.tick,
                      )
                  : undefined
              }
            />
            <Button
              icon={ICONS.XmarkLight}
              iconColor={resultIsCross ? 'white' : theme.colors.red}
              style={{
                backgroundColor: resultIsCross ? theme.colors.red : 'white',
                borderWidth: 0,
                flex: sizeIsSmall ? 1 : undefined,
                width: sizeIsSmall ? undefined : 40,
                borderRadius: 0,
                justifyContent: 'center',
              }}
              styleHovered={{
                backgroundColor: resultIsCross
                  ? theme.colors.red80
                  : theme.colors.red10,
              }}
              stylePressed={{
                backgroundColor: resultIsCross
                  ? theme.colors.red90
                  : theme.colors.red20,
              }}
              onPress={
                handleSaveChecklistItemResult
                  ? () =>
                      handleSaveChecklistItemResult(
                        checklistItem.id,
                        ChecklistItemResultEnum.cross,
                      )
                  : undefined
              }
            />
            <Button
              icon={ICONS.DashLight}
              style={{
                backgroundColor: resultIsNA ? '#999' : 'white',
                borderWidth: 0,
                flex: sizeIsSmall ? 1 : undefined,
                width: sizeIsSmall ? undefined : 40,
                borderRadius: 0,
                justifyContent: 'center',
              }}
              iconSize={22}
              iconColor={resultIsNA ? 'white' : 'black'}
              styleHovered={{
                backgroundColor: resultIsNA ? '#999999cc' : '#9999991a',
              }}
              stylePressed={{
                backgroundColor: resultIsNA ? '#999999e6' : '#99999932',
              }}
              onPress={
                handleSaveChecklistItemResult
                  ? () =>
                      handleSaveChecklistItemResult(
                        checklistItem.id,
                        ChecklistItemResultEnum.dash,
                      )
                  : undefined
              }
            />
            <Button
              icon={ICONS.TrashCanLight}
              style={{
                backgroundColor: 'transparent',
                borderRadius: 0,
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
              }}
              iconColor={
                !checklistItemResult ? theme.colors.placeholder : undefined
              }
              buttonTheme={ButtonTheme.themeless}
              onPress={
                handleSaveChecklistItemResult
                  ? () => handleSaveChecklistItemResult(checklistItem.id, null)
                  : undefined
              }
              disabled={!checklistItemResult}
            />
          </ViewWithSeparators>
        )}
      </View>
      {suggestionsUnderChecklistItems &&
        (!!actionSuggestions.length || !!controlSuggestions.length) && (
          <Card
            labelSize="xxsmall"
            margin={false}
            marginHorizontal={false}
            style={{ marginTop: theme.paddingHorizontal }}
            bodyStyle={{
              borderColor: '#f1f1f1',
              borderWidth: 1,
            }}
          >
            <ViewWithSeparators>
              {actionSuggestions.map((action) => (
                <AddTemplateAction
                  key={action.id}
                  parentId={questionId}
                  parentType="question"
                  templateId={action.id}
                  fieldInstanceId={fieldInstanceId}
                  parent={parent}
                />
              ))}
              {controlSuggestions.map((control) => (
                <AddTemplateControl
                  key={control.id}
                  parentId={questionId}
                  parentType="question"
                  templateId={control.id}
                  fieldInstanceId={fieldInstanceId}
                  parent={parent}
                />
              ))}
            </ViewWithSeparators>
          </Card>
        )}
    </CardRow>
  );
};
